import React from 'react'
import { Box, Flex, FlexProps, Skeleton, Text } from '@chakra-ui/react'
import { chakra } from '@chakra-ui/system'
import {
    Briefcase as BriefcaseIcon,
    Clock as ClockIcon,
    Globe as GlobeIcon,
    MapPin as MapPinIcon,
} from '@emotion-icons/feather'
import { formatDistanceToNow } from 'date-fns'
import { Company, getCompanyMainLocationCountry } from 'entities/company'
import {
    Job,
    RemoteInfo,
    formatJobLocation,
    formatJobRegionRestrictions,
    formatJobTimezoneRestrictions,
} from 'entities/job'
import { job as mockJob } from 'entities/job.mock'
import { Location } from 'entities/location'
import { skeletonLocation } from 'entities/location.mock'
import { skeletonTimezone } from 'entities/timezone.mock'
import { Heading } from 'components/Heading'
import { Avatar } from 'components/images/Avatar'

const BriefcaseIconChakra = chakra(BriefcaseIcon)
const MapPinIconChakra = chakra(MapPinIcon)
const ClockIconChakra = chakra(ClockIcon)
const GlobeIconChakra = chakra(GlobeIcon)

type CompanyForJobHeader = Pick<Company, 'name' | 'logo'> & {
    locations?: Pick<Location, 'country'>[]
}

type JobForJobHeader = Pick<Job, 'title' | 'location' | 'remote' | 'lastModified'> & {
    company: CompanyForJobHeader
    remoteInfo?: Pick<RemoteInfo, 'regionRestrictions' | 'timezoneRestrictions'>
}

const CompanyLogo = ({ company }: { company?: CompanyForJobHeader }) => {
    const { logo = mockJob.company.logo } = company ?? {}

    const isLoaded = Boolean(company)

    if (!logo) {
        return null
    }

    return (
        <Avatar
            variant="company"
            src={logo}
            // NB: It's '40px' on mobile mockups, but we also display
            //     a company location below the company's name, so it
            //     has to be bigger than that.
            boxSize="68px"
            sx={{
                width: { base: '52px', sm: '68px' },
                height: { base: '52px', sm: '68px' },
            }}
            shadow="outline_gray_sm"
            isLoaded={isLoaded}
        />
    )
}

const CompanyDetailsSnippet = ({
    company,
    ...flexProps
}: {
    company?: CompanyForJobHeader
} & FlexProps) => {
    const isLoaded = Boolean(company)

    return (
        <Flex gridGap="13px" {...flexProps}>
            <CompanyLogo company={company} />
            <Flex alignSelf="center" direction="column" gridGap="1px">
                <Skeleton isLoaded={isLoaded}>
                    <Heading as="h2" variant={'jobPageTitle'} noOfLines={1}>
                        {company?.name ?? mockJob.company.name}
                    </Heading>
                </Skeleton>
                <Skeleton isLoaded={isLoaded}>
                    <Text
                        variant="bodyTextSmall"
                        color="text.secondary"
                        // NB: For "New York, United States of America",
                        //     on `global-min=320px`, it otherwise stretches the UI.
                        overflowWrap="anywhere"
                        noOfLines={1}
                    >
                        {isLoaded
                            ? getCompanyMainLocationCountry(company)
                            : skeletonLocation.country}
                    </Text>
                </Skeleton>
            </Flex>
        </Flex>
    )
}

const JobMostImportantDetails = ({
    job,
    isLoaded,
}: {
    job?: JobForJobHeader
    isLoaded: boolean
}) => {
    const { remote, lastModified = mockJob.lastModified } = job ?? {}

    let jobLocationOrRegionRestrictions
    let jobTimezoneRestrictions

    if (job) {
        if (remote) {
            jobLocationOrRegionRestrictions = formatJobRegionRestrictions(job)
            jobTimezoneRestrictions = formatJobTimezoneRestrictions(job)
        } else {
            jobLocationOrRegionRestrictions = formatJobLocation(job)
        }
    }

    const skeletonText = `Remote within ${skeletonLocation.country}`

    const showTimezoneDelta = !isLoaded || Boolean(jobTimezoneRestrictions)
    const showLocationInfo = !isLoaded || jobLocationOrRegionRestrictions
    const isWorldwide = remote && !Boolean(job?.remoteInfo?.regionRestrictions?.length)

    return (
        <Flex
            direction={{ base: 'column', sm: 'row' }}
            flexWrap={'wrap'}
            gridColumnGap={{ base: '6px', sm: '19px' }}
            gridRowGap={'6px'}
        >
            {showLocationInfo && (
                <Flex gridGap="6px" align="flex-start">
                    {isWorldwide ? (
                        <GlobeIconChakra
                            flexShrink={0}
                            color="text.secondary"
                            width="19px"
                            strokeWidth={1.5}
                        />
                    ) : (
                        <MapPinIconChakra
                            flexShrink={0}
                            color="text.secondary"
                            width="19px"
                            strokeWidth={1.5}
                        />
                    )}
                    <Skeleton isLoaded={isLoaded}>
                        <Text
                            variant="functionalNormal"
                            letterSpacing="-0.01em"
                            color="text.secondary"
                        >
                            {isLoaded ? jobLocationOrRegionRestrictions : skeletonText}
                        </Text>
                    </Skeleton>
                </Flex>
            )}
            {showTimezoneDelta && (
                <Flex gridGap="6px" align="flex-start">
                    <ClockIconChakra
                        flexShrink={0}
                        color="text.secondary"
                        width="19px"
                        strokeWidth={1.5}
                    />
                    <Skeleton isLoaded={isLoaded}>
                        <Text
                            variant="functionalNormal"
                            letterSpacing="-0.01em"
                            color="text.secondary"
                        >
                            {jobTimezoneRestrictions ?? skeletonTimezone}
                        </Text>
                    </Skeleton>
                </Flex>
            )}
            {/* TODO [DEV-968]: Append timezone restrictions. */}
            <Flex gridGap="6px" align="flex-start">
                <BriefcaseIconChakra
                    flexShrink={0}
                    color="text.secondary"
                    width="19px"
                    strokeWidth={1.5}
                />
                <Skeleton isLoaded={isLoaded}>
                    <Text
                        variant="functionalNormal"
                        letterSpacing="-0.01em"
                        color="text.secondary"
                        whiteSpace="nowrap"
                    >
                        {isLoaded
                            ? `Posted ${formatDistanceToNow(new Date(lastModified), {
                                  addSuffix: true,
                              })}`
                            : 'Posted a long time ago'}
                    </Text>
                </Skeleton>
            </Flex>
        </Flex>
    )
}

export const JobHeader = ({ job }: { job?: JobForJobHeader }) => {
    const { title = mockJob.title, company } = job ?? {}

    const isLoaded = Boolean(job?.title)

    return (
        <Flex gridGap="16px">
            <Box display={{ base: 'none', sm: 'contents' }}>
                <CompanyLogo company={company} />
            </Box>
            <Flex direction="column" gridGap={{ base: '12px', sm: '8px' }}>
                <Skeleton isLoaded={isLoaded}>
                    <Heading as="h1" size="md" noOfLines={2}>
                        {title}
                    </Heading>
                </Skeleton>
                <CompanyDetailsSnippet
                    display={{ base: 'flex', sm: 'none' }}
                    mt={{ base: '8px', sm: '0' }}
                    company={company}
                />
                <JobMostImportantDetails job={job} isLoaded={isLoaded} />
            </Flex>
        </Flex>
    )
}
